import { Flex } from "@hackthenorth/north";
import React from "react";
import "twin.macro";
import styled from "styled-components";

import { TooltipWrapper } from "src/shared/components";
import Icon, { IconName } from "src/shared/components/Icon";
import PerkStatusTooltip from "src/views/sponsor/perks/components/PerkStatusTooltip";

import { useSponsorPerksContext } from "../../SponsorPerksContext";
import { PerkStatus } from "../../types";
import { PERKS_TO_DUE_DATES } from "../../types";
import {
  getDaysUntilDueDate,
  getDueColor,
  getDueDateBadge,
} from "../Perk/helpers";

import { Label } from "north.js";
import "styled-components/macro";

interface Props {
  perksArr: {
    perkSlug: string;
    perkName: string;
    iconName: IconName;
    dueDate: string;
    status: PerkStatus;
  }[];
  updateSelectedPerk: (perk: string) => void;
  selectedPerk: string | null;
}

const PerkList: React.FC<Props> = ({
  perksArr,
  updateSelectedPerk,
  selectedPerk,
}) => {
  const { perks } = useSponsorPerksContext();
  return (
    <Container>
      <PerkListContainer>
        {perksArr.map(({ perkSlug, perkName, iconName, status }, idx) => {
          const dueDateSlug = PERKS_TO_DUE_DATES[perkSlug];
          const dueDateValue = perks?.[dueDateSlug as any]?.value ?? null;

          const daysUntilDue = getDaysUntilDueDate(dueDateValue);
          const dueColor = getDueColor(daysUntilDue);
          const dueDateBadge = getDueDateBadge(dueDateValue);

          return (
            <StyledButton
              onClick={() => updateSelectedPerk(perkSlug)}
              key={perkSlug}
              onKeyDown={() => updateSelectedPerk(perkSlug)}
              tabIndex={idx}
            >
              <PerkItem selected={perkSlug === selectedPerk}>
                <PerkStatusTooltip
                  id={`perk-list-status-tooltip-${perkSlug}`}
                  status={status}
                >
                  <PerkStatusIcon size="18px" name={iconName} />
                </PerkStatusTooltip>
                {perkName}
                {daysUntilDue !== null &&
                  status === PerkStatus.INCOMPLETE &&
                  dueColor && (
                    <TooltipWrapper
                      id={`perk-list-due-date-tooltip-${perkSlug}`}
                      icon={
                        <Label color={dueColor} size="sm">
                          <Icon name="calendar" size="12px" color="white" />
                        </Label>
                      }
                      content={dueDateBadge}
                      direction="top"
                      color="textDark"
                      padding={12}
                    />
                  )}
              </PerkItem>
            </StyledButton>
          );
        })}
      </PerkListContainer>
    </Container>
  );
};

const Container = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 25px;
  &:focus {
    outline: none;
  }
`;

const StyledButton = styled.button`
  &:focus {
    outline: none;
  }
`;

const PerkListContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
`;

const PerkStatusIcon = styled(Icon)`
  flex-shrink: 0;
  transform: translateY(1px);
`;

const PerkItem = styled(Flex).attrs({
  row: true,
  align: "center",
  justify: "start",
})<{ isSelected: boolean }>`
  padding: 8px 10px 8px 30px;
  color: ${({ theme, selected }) =>
    selected
      ? theme.globalConstants.color.textBody
      : theme.globalConstants.color.textSecondary};
  background-color: ${({ theme, selected }) =>
    selected
      ? theme.globalConstants.color.backgroundGrayDark
      : theme.globalConstants.color.white};
  font-size: 16px;
  font-weight: ${({ selected }) => (selected ? 500 : 400)};
  line-height: 22px;
  cursor: pointer;
  border: none !important;
  column-gap: 10px;
  &::-webkit-scrollbar {
    display: none !important;
  }
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default PerkList;
